import { useState } from 'react';

import {
  type FrequencyType,
  type GroupClassIllustrations,
  ChakraBox,
  EmptySectionCard,
  FAIconName,
  GroupClassListViewCard,
  GroupSectionListViewCard,
  SkeletonLoader,
  TagColor,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import type { AvatarUserType } from '@equip.health/ui/lib/types/src/components/Avatar';

import { GroupClassKebabMenuTitle } from '~/components/maud/calendar/group-class/GroupClassListView';
import GroupClassDetailsModal from '~/components/maud/calendar/group-class/modals/GroupClassDetailsModal';
import {
  getPreferredFirstName,
  sortPatientsAndSupports,
} from '~/lib/util/patient.util';
import { useLazyGetGroupClassDetailQuery } from '~/store/services/groupClass';
import {
  useGetPatientAndSupportRegisteredGroupClassesQuery,
  useGetPatientAndSupportRegisteredGroupClassesV2Query,
  useGetPatientQuery,
} from '~/store/services/patients';

import UnregisterPatientModal from './modals/UnregisterPatientModal';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { PatientCalendarEvents } from '~/analytics/patientsCalendarEvents';
import { GroupSectionEvents } from '~/analytics/groupSectionEvents';
import type { AnalyticsEvent } from '~/analytics';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import type { GroupClassKebabItem } from '~/lib/types/classes/GroupClassKebabItem';
import GroupClassDetailsDrawer from '~/components/maud/calendar/groupsV2/GroupClassDetailsView';
import {
  type GroupTypeEnum,
  GroupSectionFrequency,
  GroupTypeTagColorMapper,
} from '~/lib/constants/groupClasses.constants';
import {
  formatGroupSectionDuration,
  formatGroupSectionRecurrence,
  formatGroupSectionTimeSlot,
  generateHostInitials,
} from '~/lib/util/groups.utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import RegisterUserForGroupModal from './modals/RegisterUserForGroupModal';
import { SYSTEM_TIME_ZONE } from '~/lib/util/calendar.util';

type Modal = 'modal' | 'list';

const TOP_LEVEL_ID = 'patient-group-class-list';

const getGroupTypeTagColor = (groupType?: string) => {
  if (!groupType) return TagColor.gray;

  return GroupTypeTagColorMapper[groupType as GroupTypeEnum] ?? TagColor.gray;
};

const getFormattedSchedule = (groupClass: GroupClassBaseV2) => {
  if (groupClass.frequency === GroupSectionFrequency.DOES_NOT_REPEAT) {
    return null;
  }
  return (
    formatGroupSectionRecurrence({
      start: groupClass.startDate,
      frequency: groupClass.frequency as SectionFrequencyCode,
    }) ?? String(groupClass.frequency ?? '')
  );
};

const getFormattedTimeSlot = (groupClass: GroupClassBaseV2) => {
  if (!groupClass.startDate || !groupClass.duration) return '';

  return formatGroupSectionTimeSlot({
    start: groupClass.startDate,
    duration: groupClass.duration,
    timeZone: SYSTEM_TIME_ZONE,
  });
};

const getFormattedDurationWeeks = (groupClass: GroupClassBaseV2) => {
  if (groupClass.length) return groupClass.length;
  if (!groupClass.startDate || !groupClass.endDate) return '';

  return (
    formatGroupSectionDuration({
      startDate: groupClass.startDate,
      endDate: groupClass.endDate,
    }) ?? ''
  );
};

const ANALYTICS_SOURCE = AnalyticsSourceValues.PATIENT_CALENDAR_TAB;

const getGroupAnalyticsProperties = (groupClass?: GroupClassBaseV2) => ({
  Source: ANALYTICS_SOURCE,
  'Group title': groupClass?.title,
  'Section day': groupClass?.dayOfWeek,
  'Section time': groupClass?.startDate,
});

const createTooltipWrapper =
  (actionTime: string) =>
  ({
    children,
    providerExternalId,
  }: {
    children: JSX.Element;
    providerExternalId: string;
  }) => (
    <ProviderAvatarTooltipWrapper
      actionTime={actionTime}
      isRoleAtAction
      placement="left"
      providerExternalId={providerExternalId}
    >
      {children}
    </ProviderAvatarTooltipWrapper>
  );

export type PatientGroupClassListItem = {
  firstName: string;
  lastName: string;
  externalId: string;
  name?: string;
  chosenName?: string;
  userType: string;
  groups?: GroupClassBaseV2[];
};

type ChangeSectionData = {
  groupClassExternalId: string;
  groupSectionId: string;
  userId: string;
};

const PatientGroupClassesListView = ({ patientId }: { patientId: string }) => {
  const [getGroupClassDetail, { data: groupClassDetail }] =
    useLazyGetGroupClassDetailQuery();
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();
  const [isGroupClassDetailModalOpen, setIsGroupClassDetailModalOpen] =
    useState<boolean>(false);

  const [selectedGroupClass, setSelectedGroupClass] =
    useState<SelectedGroupClassData | null>();
  const [isUnregisteredModalOpen, setIsUnregisteredModalOpen] =
    useState<boolean>(false);

  const [selectedGroupDrawerId, setSelectedGroupDrawerId] = useState<
    string | null
  >(null);

  const [changeSectionData, setChangeSectionData] =
    useState<ChangeSectionData>();

  const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();

  const { data: Patient } = useGetPatientQuery(
    { patientId },
    {
      skip: !isGroupsRestructuringEnabled || patientId === undefined,
    },
  );

  const { isLoading, data: groupClasses } =
    useGetPatientAndSupportRegisteredGroupClassesQuery(
      { patientId },
      { skip: isGroupsRestructuringEnabled || patientId === undefined },
    );

  const { isLoading: isV2Loading, data: groupClassesV2 } =
    useGetPatientAndSupportRegisteredGroupClassesV2Query(
      { patientId },
      { skip: !isGroupsRestructuringEnabled || patientId === undefined },
    );

  const groupClassData = isGroupsRestructuringEnabled
    ? (groupClassesV2 ?? [])
    : (groupClasses?.data ?? []);
  const isGroupClassesLoading = isGroupsRestructuringEnabled
    ? isV2Loading
    : isLoading;

  const findGroupClassById = (groupClassExternalId: string) =>
    groupClassData
      .flatMap((user) => user.groups || [])
      .find((g) => g.groupClassExternalId === groupClassExternalId);

  const trackGroupEvent = (
    event: AnalyticsEvent,
    groupClassExternalId?: string,
    additionalProps?: Record<string, unknown>,
  ) => {
    const groupClass = groupClassExternalId
      ? findGroupClassById(groupClassExternalId)
      : undefined;
    trackEvent(event, {
      ...getGroupAnalyticsProperties(groupClass),
      ...additionalProps,
    });
  };

  const handleCardClick = (groupClassExternalId: string, userId: string) => {
    trackEvent(PatientCalendarEvents.groupsListCardButtonClicked, {
      Source: ANALYTICS_SOURCE,
    });
    if (isGroupsRestructuringEnabled) {
      setSelectedGroupDrawerId(groupClassExternalId);
      trackEvent(GroupSectionEvents.groupClassDetailsDrawerViewed, {
        Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
      });
    } else {
      viewGroupClassDetail({ groupClassExternalId, userId });
    }
  };

  const handleErrorToast = () => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: `${TOP_LEVEL_ID}__warning`,
      text: "We're sorry, we were unable to process your request at this time. Please try again.",
      variant: 'critical',
    });
  };

  const getHostDetails = (hostsList: GroupClassHost[]) =>
    hostsList.map((host: GroupClassHost) => ({
      ...host,
      name: generateHostInitials(host),
      userType: host.type as AvatarUserType,
      src: host?.avatar?.thumbnailUrl ?? host?.avatar?.url,
      id: host.externalId,
    }));

  const viewGroupClassDetail = ({
    groupClassExternalId,
    userId,
  }: SelectedGroupClassData) => {
    setSelectedGroupClass({ groupClassExternalId, userId });
    getGroupClassDetail({ groupClassExternalId })
      .unwrap()
      .then(() => {
        setIsGroupClassDetailModalOpen(true);
      })
      .catch(handleErrorToast);
  };

  const unregisterGroupClass = ({
    groupClassExternalId,
    groupSectionId,
    userId,
  }: SelectedGroupClassData) => {
    setSelectedGroupClass({ groupClassExternalId, groupSectionId, userId });
    setIsUnregisteredModalOpen(true);
  };

  const handleKebabOption = (
    {
      groupClassExternalId,
      groupSectionId,
      command,
      userId,
    }: SelectedGroupClassData,
    source?: Modal,
  ) => {
    switch (command) {
      case GroupClassKebabMenuTitle.view:
        trackEvent(PatientCalendarEvents.groupsListKebabMenuViewButtonClicked, {
          Source: ANALYTICS_SOURCE,
        });
        if (isGroupsRestructuringEnabled) {
          setSelectedGroupDrawerId(groupClassExternalId);
          trackEvent(GroupSectionEvents.groupClassDetailsDrawerViewed, {
            Source: AnalyticsSourceValues.CALENDAR_GROUP_SECTION_TAB,
          });
        } else {
          viewGroupClassDetail({ groupClassExternalId, userId });
        }
        break;
      case GroupClassKebabMenuTitle.unregister:
        trackGroupEvent(
          source === 'list'
            ? PatientCalendarEvents.groupsListKebabMenuUnRegisterButtonClicked
            : PatientCalendarEvents.groupsListDetailModalKebabMenuUnRegisterButtonClicked,
          groupClassExternalId,
        );
        unregisterGroupClass({ groupClassExternalId, groupSectionId, userId });
        break;
    }
  };

  const getKebabMenuOptions = (
    {
      groupClassExternalId,
      groupSectionId,
      userId,
      groupActiveSectionCount,
    }: SelectedGroupClassData,
    source?: Modal,
  ) => {
    const kebabItems: Array<GroupClassKebabItem> = [
      ...(isGroupsRestructuringEnabled && (groupActiveSectionCount ?? 0) > 1
        ? [
            {
              label: 'Choose a different section',
              leftIcon: FAIconName.calendarPenRegular,
              leftIconColor: 'neutral.primary',
              onClick: () => {
                trackGroupEvent(
                  PatientCalendarEvents.groupsListKebabMenuChooseDifferentSectionButtonClicked,
                  groupClassExternalId,
                );
                setChangeSectionData({
                  groupClassExternalId,
                  groupSectionId,
                  userId,
                });
              },
            },
          ]
        : []),

      {
        label: 'Unregister user from group',
        leftIcon: FAIconName.userSlashRegular,
        leftIconColor: 'neutral.primary',
        onClick: () =>
          handleKebabOption(
            {
              command: GroupClassKebabMenuTitle.unregister,
              groupClassExternalId,
              groupSectionId,
              userId,
            },
            source,
          ),
      },
    ];
    if (!isGroupClassDetailModalOpen) {
      kebabItems.push({
        label: GroupClassKebabMenuTitle.view,
        leftIcon: FAIconName.infoCircleRegular,
        leftIconColor: 'neutral.primary',
        onClick: () =>
          handleKebabOption({
            command: GroupClassKebabMenuTitle.view,
            groupClassExternalId,
            userId,
          }),
      });
    }

    return kebabItems;
  };

  const handleGroupClassDetailViewModalClose = () => {
    setIsGroupClassDetailModalOpen(false);
    setSelectedGroupClass(null);
  };

  const handleunregisterModalClose = () => {
    setIsUnregisteredModalOpen(false);
    setIsGroupClassDetailModalOpen(false);
  };

  return (
    <>
      <SkeletonLoader id={TOP_LEVEL_ID} isLoaded={!isGroupClassesLoading}>
        <ChakraBox>
          {sortPatientsAndSupports(groupClassData)?.map((each) => {
            const patientOrSupportName = getPreferredFirstName(
              each.chosenName,
              each.firstName,
              false,
            );
            return (
              <ChakraBox
                key={`${TOP_LEVEL_ID}__${each.externalId}`}
                marginBottom="24px"
              >
                <ChakraBox marginBottom="12px" paddingLeft="5px">
                  <Text
                    color="neutral.secondary"
                    id={`${TOP_LEVEL_ID}__patient-or-support-name`}
                    variant="caption"
                  >
                    {patientOrSupportName} {`(${each.userType})`}
                  </Text>
                </ChakraBox>
                {each.groups && each.groups.length > 0 ? (
                  each.groups.map((groupClass) => (
                    <ChakraBox
                      key={`${TOP_LEVEL_ID}__group-class-list-item__${each.externalId}__${groupClass.groupClassExternalId}`}
                      padding="6px 5px"
                    >
                      {isGroupsRestructuringEnabled ? (
                        <GroupSectionListViewCard
                          durationWeeks={getFormattedDurationWeeks(groupClass)}
                          groupType={groupClass.groupType ?? 'Group'}
                          groupTypeTagColor={getGroupTypeTagColor(
                            groupClass.groupTypeCode,
                          )}
                          hosts={getHostDetails(groupClass.hosts)}
                          iconName={
                            groupClass.iconName as GroupClassIllustrations
                          }
                          id={`${TOP_LEVEL_ID}__group-class-list-item__${groupClass.groupClassExternalId}`}
                          kebabItems={getKebabMenuOptions(
                            {
                              groupClassExternalId:
                                groupClass.groupClassExternalId,
                              groupSectionId: groupClass.groupSectionId,
                              groupActiveSectionCount:
                                groupClass.groupActiveSectionCount,
                              userId: each.externalId,
                            },
                            'list',
                          )}
                          onCardClick={() =>
                            handleCardClick(
                              groupClass.groupClassExternalId,
                              each.externalId,
                            )
                          }
                          schedule={getFormattedSchedule(groupClass)}
                          timeSlot={getFormattedTimeSlot(groupClass)}
                          title={groupClass.title}
                          tooltipWrapper={createTooltipWrapper(
                            groupClass.startDate,
                          )}
                        />
                      ) : (
                        <GroupClassListViewCard
                          endDate={groupClass.endDate}
                          frequency={groupClass.frequency as FrequencyType}
                          groupClassExternalId={groupClass.groupClassExternalId}
                          hosts={getHostDetails(groupClass.hosts)}
                          iconName={
                            groupClass.iconName as GroupClassIllustrations
                          } // TODO investigate if this should be typed as such higher up, for example at network call level
                          id={`${TOP_LEVEL_ID}__group-class-list-item__${groupClass.groupClassExternalId}`}
                          isArchived={groupClass.isArchived}
                          isHost={groupClass.isHost}
                          isSensitive={groupClass.isSensitive}
                          isUserRegistered={groupClass.isUserRegistered}
                          kebabItems={getKebabMenuOptions(
                            {
                              groupClassExternalId:
                                groupClass.groupClassExternalId,
                              userId: each.externalId,
                            },
                            'list',
                          )}
                          nextStartDate={groupClass.nextStartDate}
                          onCardClick={() =>
                            handleCardClick(
                              groupClass.groupClassExternalId,
                              each.externalId,
                            )
                          }
                          startDate={groupClass.startDate}
                          title={groupClass.title}
                          tooltipWrapper={createTooltipWrapper(
                            groupClass.startDate,
                          )}
                        />
                      )}
                    </ChakraBox>
                  ))
                ) : (
                  <ChakraBox padding="6px 5px">
                    <EmptySectionCard
                      caption={`${patientOrSupportName} isn't registered for any groups`}
                      id={`${TOP_LEVEL_ID}__empty-card`}
                    />
                  </ChakraBox>
                )}
              </ChakraBox>
            );
          })}
        </ChakraBox>
      </SkeletonLoader>
      {selectedGroupDrawerId && (
        <GroupClassDetailsDrawer
          groupClassId={selectedGroupDrawerId}
          isOpen
          onClose={() => setSelectedGroupDrawerId(null)}
        />
      )}
      {!isGroupsRestructuringEnabled && groupClassDetail && (
        <GroupClassDetailsModal
          groupClass={{
            ...groupClassDetail.data,
            isHost: false,
            isUserRegistered: true,
          }}
          isOpen={isGroupClassDetailModalOpen}
          kebabOptions={getKebabMenuOptions({
            groupClassExternalId: groupClassDetail.data.groupClassExternalId,
            userId: selectedGroupClass?.userId ?? '',
          })}
          onClose={handleGroupClassDetailViewModalClose}
        />
      )}
      {selectedGroupClass && (
        <UnregisterPatientModal
          groupClassExternalId={selectedGroupClass.groupClassExternalId}
          groupSectionId={selectedGroupClass.groupSectionId}
          isOpen={isUnregisteredModalOpen}
          onClose={handleunregisterModalClose}
          patientId={patientId}
          userId={selectedGroupClass.userId}
        />
      )}

      {changeSectionData && (
        <RegisterUserForGroupModal
          groupId={changeSectionData.groupClassExternalId}
          sectionId={changeSectionData.groupSectionId}
          userId={changeSectionData.userId}
          isOpen
          onClose={() => setChangeSectionData(undefined)}
          patient={Patient}
        />
      )}
    </>
  );
};

export default PatientGroupClassesListView;
